// angular
import { Meta, Title } from '@angular/platform-browser';
import { Component, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// components
import { ProductPreviewComponent } from './components/product-preview/product-preview.component';
import { ProductNavigationTabsComponent } from './components/product-navigation-tabs/product-navigation-tabs.component';
import { ProductDescriptionComponent } from './components/product-description/product-description.component';
import { ProductVideoComponent } from './components/product-video/product-video.component';
import { ProductClimaticPropertiesComponent } from './components/product-climatic-properties/product-climatic-properties.component';
import { ProductAdditionalPropertiesComponent } from './components/product-additional-properties/product-additional-properties.component';
import { ProductUsageComponent } from './components/product-usage/product-usage.component';
import { ProductExpertOpinionsComponent } from './components/product-expert-opinions/product-expert-opinions.component';
import { ProductCommentsComponent } from './components/product-comments/product-comments.component';
import { FaqSectionComponent } from '@app/core/components/faq-section/faq-section.component';
import { CommentFormBoxComponent } from '@app/core/components/comment-form-box/comment-form-box.component';
import { ProductTagsComponent } from './components/product-tags/product-tags.component';
import { ProductDetailsSkeletonComponent } from './components/product-details-skeleton/product-details-skeleton.component';
import { BreadcrumbsComponent } from '@app/core/components/breadcrumbs/breadcrumbs.component';
import { OptionsMenuComponent } from '@app/core/components/options-menu/options-menu.component';
import { BarcodeDialogComponent } from '@app/core/dialogs/barcode-dialog/barcode-dialog.component';
import { SignupDialogComponent } from '@app/core/dialogs/signup-dialog/signup-dialog.component';
// services
import { ProductsService } from '../shared/services/products.service';
import { StorageService } from '@app/shared/services/storage.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { FavoritesService } from '@app/+profile/profile-favorites/shared/favorites.service';
import { UserService } from '@app/shared/services/user.service';
// interfaces & types
import {
  IFaq,
  IProduct,
  PriceType,
  ProductLifeStyle,
} from '../shared/interfaces/product.interface';
import { ISectionTab } from '@app/core/shared/interfaces/section-tab.interface';
import { AdditionalSpecificationType } from '../shared/interfaces/additional-specification.interface';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { IMedia } from '@app/shared/interfaces/media.interface';
import { IBreadcrumbs } from '@app/shared/interfaces/breadcrumbs.interface';
import { ExpertOpinionsType } from '../shared/types/expert-opinions.type';
import { ProductClimaticType } from '../shared/types/product-climatic.type';
import { ProductVideoType } from '../shared/types/product-video.type';
import {
  PreviewMenuOptionsType,
  ProductPreviewType,
} from '../shared/types/product-preview.type';
//  enums & env
import { I18n } from '@app/shared/enums/i18n.enum';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { environment } from '@environments/environment.prod';
// constants
import { productBreadCrumbs } from '../shared/constants/product-breadcrumbs-items';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';

@Component({
  selector: 'pwj-products-detail',
  standalone: true,
  imports: [
    ProductPreviewComponent,
    ProductTagsComponent,
    ProductNavigationTabsComponent,
    ProductDescriptionComponent,
    ProductVideoComponent,
    ProductClimaticPropertiesComponent,
    ProductAdditionalPropertiesComponent,
    ProductUsageComponent,
    ProductExpertOpinionsComponent,
    FaqSectionComponent,
    ProductCommentsComponent,
    CommentFormBoxComponent,
    ProductDetailsSkeletonComponent,
    BreadcrumbsComponent,
    OptionsMenuComponent,
    TranslateDataPipe,
  ],
  templateUrl: './products-detail.component.html',
  styleUrl: './products-detail.component.scss',
})
export class ProductsDetailComponent {
  public breadcrumbsData: IBreadcrumbs[] = [];
  public menuOptions: PreviewMenuOptionsType[] = [];
  public navigationTabsData: ISectionTab[] = [];
  public alreadyAddedToFavorites!: boolean;
  public productPreviewData!: ProductPreviewType;
  public productTags: string[] = [];
  public productDescription!: II18n;
  public productVideo!: ProductVideoType;
  public climaticData!: ProductClimaticType;
  public additionalSpecificationData!: AdditionalSpecificationType;
  public usageData: ProductLifeStyle[] = [];
  public expertOpinionsData!: ExpertOpinionsType;
  public faqData!: IFaq[];
  public productId!: string;
  public isLoading!: boolean;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;
  public currentLang!: I18n;

  private _userIsLoggedIn!: boolean;

  // dependencies
  private readonly _storageService = inject(StorageService);
  private readonly _translateData = inject(TranslateDataPipe);
  private readonly _translateService = inject(TranslateService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _productsService = inject(ProductsService);
  private readonly _titleService = inject(Title);
  private readonly _metaService = inject(Meta);
  private readonly _dialogService = inject(DialogService);
  private readonly _favoritesService = inject(FavoritesService);
  private readonly _userService = inject(UserService);
  private readonly _translateDataPipe = inject(TranslateDataPipe);

  constructor() {
    // check user is logged-in or no
    effect(() => {
      this._userIsLoggedIn = !!this._userService.userSignal().username;
    });
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    // set product id
    this._activatedRoute.params.subscribe(params => {
      this.productId = params?.['id'];
    });

    // send req to api
    if (this.productId) {
      this._productsService.getProductDetails(this.productId);
    } else {
      this._router.navigate(['']);
    }

    effect(() => {
      // set loading state
      this.isLoading = this._productsService.isProductsDetailsLoading();
    });

    // set current lang
    this.currentLang = (I18n as any)[this._translateService.currentLang];
    this._translateService.onLangChange.subscribe(({ lang }) => {
      this.currentLang = (I18n as any)[lang];

      // update bread crumbs data on lang change
      this.setBreadCrumbsData();
    });

    effect(() => {
      // set bread crumbs data
      this.setBreadCrumbsData();
      // set product details data
      this.getProductDetailsData();
    });
  }

  public setBreadCrumbsData() {
    const data = this._productsService?.productDetails();

    if (data) {
      // set breadCrumbs data
      const newBreadCrumbs: IBreadcrumbs[] = [
        {
          id: 3,
          breadCrumbTitle: data?.category_type?.title?.[this.currentLang],
          breadCrumbUrl: `/products`,
          breadCrumbParams: {
            category_type_id: data?.category_type?._id,
          },
        },
        {
          id: 4,
          breadCrumbTitle: data?.title?.[this.currentLang] || data?.title?.en,
        },
      ];
      this.breadcrumbsData = [...productBreadCrumbs, ...newBreadCrumbs];
    }
  }

  public getProductDetailsData() {
    // get product details from service

    const data = this._productsService?.productDetails();

    if (data) {
      // implement navigation tabs data
      this.implementNavigationTabs(data);
      // is product already liked
      this.alreadyAddedToFavorites = data?.is_favorite;
      this.setMenuOptions();

      // set product preview data
      this.productPreviewData = {
        _id: data?._id,
        business_id: data?.business?._id as string,
        custom_url: data?.business?.custom_url,
        title: data?.title,
        subTitle: data?.business?.title as II18n,
        code: data.code,
        businessVerified: data?.business?.is_verfied as boolean,
        logo: data?.business?.logo as IMedia,
        media: [...(data?.images || []), ...(data?.videos || [])],
        description: data?.description,
        highest_price: data?.highest_price,
        lowest_price: data?.lowest_price,
        category_type: data?.category_type?.title,
        category: data?.category?.title,
        sub_category: data?.sub_category?.title,
        stone_grade: data?.stone_grade,
        specialist_verified: data?.specialist_verified,
        mobile: data?.business?.mobile?.mobile_number
          ? `${data?.business?.mobile?.prefix}${data?.business?.mobile?.mobile_number}`
          : '-',
        is_favorite: data?.is_favorite,
      };
      this.productTags = data?.tags || [];
      // set product description data
      this.productDescription = data?.description;
      // set product video data
      this.productVideo = {
        title: data.title,
        video: data.videos?.[0],
      };
      // set climatic properties data
      this.climaticData = {
        acid_resistance: data?.metadata?.acid_resistance || '-',
        frost_resistance: data?.metadata?.frost_resistance || '-',
        pressure_resistance: data?.metadata?.pressure_resistance || '-',
        water_absorption: data?.metadata?.water_absorption || '-',
        bulk_density: data?.metadata?.bulk_density,
        chemical_formula: data?.metadata?.chemical_formula || '-',
      };
      // set additional specification data
      const { life_styles, customize, ...rest } = data.metadata;
      this.additionalSpecificationData = {
        warranty: data?.metadata?.warranty,
        production_capacity: data?.metadata?.production_capacity,
        delivery_address: data?.delivery_address,
        min_order: data?.min_order,
        available_amount: data?.available_amount,
        delivery_type: data?.delivery_type,
        origin_country: data?.origin_country?.name,
        customizeSize: customize,
        shipment_terms: data?.shipment_terms,
        payment_methods: data?.payment_methods,
        main_color: data?.metadata?.main_color,
        veins: data?.metadata?.veins,
        dots: data?.metadata?.dots,
        halos: data?.metadata?.halos,
        dimensions: data?.metadata?.dimensions,
      };
      // set life style data
      this.usageData = data?.metadata?.life_styles || [];
      // set expert opinions data
      this.expertOpinionsData = {
        specialist_person: data?.specialist_person,
        business_title: data?.business?.title as II18n,
        specialist_note: data?.specialist_note,
        specialist_video: data?.specialist_video,
        specialist_photos: data?.specialist_photos,
      };
      // set faq q&a data
      this.faqData = data?.faq || [];

      const productTitle = `${this._translateService.instant('routes.productDetails')} ${this._translateData.transform(data?.title) || ''}`;
      const productDescription = data.description?.['en'];
      const productImage =
        environment.baseUrl.file +
        (data?.main_photo?.cropped_url?.toLowerCase() ||
          data?.images?.[0]?.cropped_url?.toLowerCase() ||
          data?.images?.[0]?.url);

      this.setPageTitle(productTitle);
      this.setMetaTags(productTitle, productDescription, productImage);
    }
  }

  public setPageTitle(content: string) {
    if (content) {
      this._titleService.setTitle(content);
    }
  }

  public setMetaTags(
    productTitle: string,
    productDescription?: string,
    productImage?: string
  ) {
    if (productTitle) {
      this._metaService.updateTag({
        property: 'og:title',
        content: productTitle || 'title',
      });
    }

    if (productDescription) {
      this._metaService.updateTag({
        property: 'og:description',
        content: productDescription,
      });
    }

    if (productImage) {
      this._metaService.updateTag({
        property: 'og:image',
        content: productImage,
      });
    }
    if (window?.location?.href) {
      this._metaService.updateTag({
        property: 'og:url',
        content: window.location.href,
      });
    }
  }

  public addProductToFavorite() {
    if (this._userIsLoggedIn) {
      if (this.productPreviewData._id) {
        if (!this.alreadyAddedToFavorites) {
          // set favorite mode
          this.alreadyAddedToFavorites = true;
        } else {
          // set favorite mode
          this.alreadyAddedToFavorites = false;
        }

        this.setMenuOptions();

        // call add to favorite service
        this._favoritesService.addProductToFavorite(
          this.productPreviewData._id
        );
      }
    } else {
      const dialogRef = this._dialogService.open(SignupDialogComponent, {
        width: 'none',
        height: 'none',
      });
    }
  }

  public openQrCodeDialog(barcodeContent: string) {
    this._dialogService.open(BarcodeDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'products.qrCode',
        productTitle: this.productPreviewData.title?.['en'],
        productCode: this.productPreviewData.code,
        barcodeContent,
      },
    });
  }

  public setMenuOptions() {
    // implement menu options data
    this.menuOptions = [
      {
        id: 0,
        name: 'products.favorite',
        optionClasses: '!flex-row gap-6 !text-2xl',
        icon: !this.alreadyAddedToFavorites ? 'heart' : null,
        imgIcon: this.alreadyAddedToFavorites ? 'assets/icons/heart.svg' : null,
        imgWidth: '20',
        imgHeight: '20',
        onSelectOption: () => {
          this.addProductToFavorite();
        },
      },
      {
        id: 1,
        name: 'global.share',
        optionClasses: '!flex-row gap-6 !text-2xl',
        icon: 'share',
        onSelectOption: (barcodeContent: string) => {
          this.openQrCodeDialog(barcodeContent);
        },
      },
    ];
  }

  public implementNavigationTabs(product: IProduct) {
    this.navigationTabsData = [
      {
        tabTitle: 'description',
        hiddenTab:
          (this._translateDataPipe.transform(product.description)?.length ||
            0) < 140,
      },
      {
        tabTitle: 'video',
        hiddenTab: product.videos?.length === 0,
      },
      {
        tabTitle: 'climaticCharacteristics',
      },
      {
        tabTitle: 'additionalSpecifications',
      },
      {
        tabTitle: 'lifeStyle',
        hiddenTab:
          !product.metadata.life_styles ||
          product.metadata.life_styles?.length === 0,
      },
      {
        tabTitle: 'expertOpinions',
        hiddenTab: !product?.specialist_note,
      },
      {
        tabTitle: 'faq',
        hiddenTab: !product?.faq || product?.faq?.length === 0,
      },
      {
        tabTitle: 'comments',
      },
    ];
  }
}
